import { OWNER_SELECTABLE_USER_TYPES } from "./OwnerField";
import { estimateOptionsContentWidth } from "./selectorOptionUtils";

/**
 * Shared helpers for company hierarchy single- and multi-select.
 * Both consume the same tree from GET companies/hierarchy-list
 * (root → national → branch → store).
 */

// 20px per nested level. Depth 0 is the highest company in this dropdown (the retailer
// root, or the highest level still present when a level is skipped) and sits flush.
const HIERARCHY_BASE_PADDING_PX = 8;
const HIERARCHY_INDENT_STEP_PX = 20;

export const paddingLeftForDepth = (depth) => {
  const level = typeof depth === "number" && depth > 0 ? depth : 0;
  return `${HIERARCHY_BASE_PADDING_PX + level * HIERARCHY_INDENT_STEP_PX}px`;
};

/**
 * Width the dropdown needs for the widest row in the tree.
 * Deep levels are indented, so the same label costs more space on a store row
 * than on a root row and that indentation has to be part of the estimate.
 *
 * @param {Array} retailers company hierarchy tree
 * @param {number} extraPaddingPx row controls such as checkbox / expand icon
 */
export const estimateHierarchyContentWidth = (retailers, extraPaddingPx = 0) => {
  let widestRow = 0;

  const walk = (nodes, depth) => {
    (Array.isArray(nodes) ? nodes : []).forEach((node) => {
      const indentPx = parseInt(paddingLeftForDepth(depth), 10) || 0;
      const rowWidth = estimateOptionsContentWidth(
        [node?.label],
        indentPx + extraPaddingPx
      );
      if (rowWidth > widestRow) widestRow = rowWidth;
      walk(node?.children, depth + 1);
    });
  };

  walk(retailers, 0);
  return widestRow;
};

/** Keep ancestors visible when a descendant matches the search text. */
export const filterCompanyHierarchyBySearch = (retailers, searchValue) => {
  const tree = Array.isArray(retailers) ? retailers : [];
  if (searchValue === "") {
    return tree;
  }

  const filterNode = (node) => {
    const selfMatches = String(node.label ?? "").toLowerCase().includes(searchValue);
    const children = Array.isArray(node.children) ? node.children : [];
    const filteredChildren = children.map(filterNode).filter((child) => child !== null);

    if (selfMatches === true || filteredChildren.length > 0) {
      return {
        ...node,
        children: selfMatches === true ? children : filteredChildren,
      };
    }
    return null;
  };

  return tree.map(filterNode).filter((node) => node !== null);
};

/** Node ids that should auto-expand while a search is active. */
export const collectExpandIdsForSearch = (filteredRetailers) => {
  const nextExpanded = {};

  const walk = (nodes) => {
    (Array.isArray(nodes) ? nodes : []).forEach((node) => {
      const children = Array.isArray(node.children) ? node.children : [];
      if (children.length > 0) {
        nextExpanded[node.id] = true;
        walk(children);
      }
    });
  };

  walk(filteredRetailers);
  return nextExpanded;
};

/** Count every company in the tree (any level). */
export const countSelectableCompanies = (retailers) => {
  let count = 0;

  const walk = (nodes) => {
    (Array.isArray(nodes) ? nodes : []).forEach((node) => {
      if (typeof node !== "undefined" && node !== null && typeof node.id !== "undefined") {
        count += 1;
      }
      walk(node.children);
    });
  };

  walk(retailers);
  return count;
};

/**
 * Default owner for campaign / clip / loop create.
 * Uses the logged-in user's assigned company — not a "Cyreen" label guess.
 * Prefer the hierarchy map when that company is in the tree (same label/level as the dropdown).
 */
export const getAssignedCompanyOwnerDefault = (userDetail, companyById = {}) => {
  const assignedCompany =
    typeof userDetail !== "undefined" && userDetail !== null ? userDetail.assigned_company : undefined;

  if (typeof assignedCompany === "undefined" || assignedCompany === null || typeof assignedCompany.id === "undefined") {
    return undefined;
  }

  const fromTree = typeof companyById !== "undefined" ? companyById[assignedCompany.id] : undefined;
  if (typeof fromTree !== "undefined") {
    return {
      id: fromTree.id,
      label: fromTree.label,
      company_level: fromTree.level,
    };
  }

  return {
    id: assignedCompany.id,
    label: assignedCompany.name,
    company_level: assignedCompany.level,
  };
};

/** Tenant company at the top of companies/hierarchy-list (selectable root row). */
export const getHierarchyRootOwnerDefault = (companyById = {}, hierarchyRoot) => {
  if (typeof hierarchyRoot === "undefined" || hierarchyRoot === null || typeof hierarchyRoot.id === "undefined") {
    return undefined;
  }

  const fromTree = typeof companyById !== "undefined" ? companyById[hierarchyRoot.id] : undefined;
  if (typeof fromTree !== "undefined") {
    return {
      id: fromTree.id,
      label: fromTree.label,
      company_level: fromTree.level,
    };
  }

  const label =
    typeof hierarchyRoot.label === "string" && hierarchyRoot.label !== ""
      ? hierarchyRoot.label
      : hierarchyRoot.name;
  return {
    id: hierarchyRoot.id,
    label,
    company_level: hierarchyRoot.level,
  };
};

/**
 * Create forms: hierarchy root when the Owner picker is visible; otherwise assigned company.
 */
export const getCreateFormOwnerDefault = (
  userDetail,
  { companyById = {}, hierarchyRoot, retailers = [], userType = "", usedFor = "add" } = {}
) => {
  const showHierarchyOwner = shouldShowHierarchyOwnerField({ userType, usedFor, retailers });
  if (showHierarchyOwner === true) {
    const rootDefault = getHierarchyRootOwnerDefault(companyById, hierarchyRoot);
    if (rootDefault !== undefined) {
      return rootDefault;
    }
  }
  return getAssignedCompanyOwnerDefault(userDetail, companyById);
};

/**
 * Same visibility as the old OwnerField:
 * cyreen/tenant, create only, and more than one company in the tree.
 */
export const shouldShowHierarchyOwnerField = ({ userType, usedFor, retailers }) => {
  return (
    OWNER_SELECTABLE_USER_TYPES.includes(userType) === true &&
    usedFor !== "update" &&
    countSelectableCompanies(retailers) > 1
  );
};
